import { useEffect, useState } from "react";
import { getProductSales, type ProductSales } from "../../../api/catalog";

type ProductSalesTabProps = {
  productId: string;
};

export function ProductSalesTab({ productId }: ProductSalesTabProps) {
  const [sales, setSales] = useState<ProductSales | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setIsLoading(true);
    getProductSales(productId)
      .then((data) => {
        if (!mounted) return;
        setSales(data);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Failed to load product sales:", error);
        setIsLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [productId]);

  if (isLoading) {
    return (
      <div className="p-6">
        <p className="text-sm text-muted">Chargement...</p>
      </div>
    );
  }

  if (!sales) {
    return (
      <div className="p-6">
        <p className="text-sm text-muted">Impossible de charger les statistiques de ventes</p>
      </div>
    );
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("fr-CA", {
      style: "currency",
      currency: "CAD",
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat("fr-CA").format(num);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-text">Statistiques de ventes</h3>
        <p className="text-xs text-muted">
          Chiffres de soumissions et ventes officielles pour ce produit/service
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Soumissions (Quotes) */}
        <div className="rounded-md border border-border bg-surface p-4 space-y-3">
          <h4 className="text-sm font-medium text-text">Soumissions</h4>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs text-muted">Quantité totale</span>
              <span className="text-sm font-semibold text-text">{formatNumber(sales.quotes.total_quantity)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-muted">Montant total</span>
              <span className="text-sm font-semibold text-text">{formatCurrency(sales.quotes.total_amount)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-muted">Nombre de soumissions</span>
              <span className="text-sm font-semibold text-text">{sales.quotes.count}</span>
            </div>
          </div>
        </div>

        {/* Soumissions acceptées (Officiellement vendu) */}
        <div className="rounded-md border border-border bg-surface p-4 space-y-3">
          <h4 className="text-sm font-medium text-text">Soumissions acceptées (Officiellement vendu)</h4>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs text-muted">Quantité totale</span>
              <span className="text-sm font-semibold text-success">{formatNumber(sales.accepted_quotes.total_quantity)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-muted">Montant total</span>
              <span className="text-sm font-semibold text-success">{formatCurrency(sales.accepted_quotes.total_amount)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-muted">Nombre de soumissions</span>
              <span className="text-sm font-semibold text-text">{sales.accepted_quotes.count}</span>
            </div>
          </div>
        </div>

        {/* Factures */}
        <div className="rounded-md border border-border bg-surface p-4 space-y-3">
          <h4 className="text-sm font-medium text-text">Factures</h4>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs text-muted">Quantité totale</span>
              <span className="text-sm font-semibold text-text">{formatNumber(sales.invoices.total_quantity)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-muted">Montant total</span>
              <span className="text-sm font-semibold text-text">{formatCurrency(sales.invoices.total_amount)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-muted">Nombre de factures</span>
              <span className="text-sm font-semibold text-text">{sales.invoices.count}</span>
            </div>
          </div>
        </div>

        {/* Factures payées */}
        <div className="rounded-md border border-border bg-surface p-4 space-y-3">
          <h4 className="text-sm font-medium text-text">Factures payées</h4>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs text-muted">Quantité totale</span>
              <span className="text-sm font-semibold text-success">{formatNumber(sales.paid_invoices.total_quantity)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-muted">Montant total</span>
              <span className="text-sm font-semibold text-success">{formatCurrency(sales.paid_invoices.total_amount)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-muted">Nombre de factures</span>
              <span className="text-sm font-semibold text-text">{sales.paid_invoices.count}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
